/**
 * Suggested Questions Component
 *
 * Displays suggested questions from the JSON file as guides (not limitations).
 * Users can click on questions to use them, but they're free to ask anything.
 */

import React, { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import Icon from '@/src/@core/components/icon'
import { useAuth } from '@/src/hooks/useAuth'
import { getUserRestaurants } from '@/src/utils/restaurantAccessUtils'
import suggestedQuestionsData from '../data/suggestedQuestions.json'

interface SuggestedQuestionsProps {
  onQuestionClick: (question: string) => void
}

interface Question {
  id: string
  text: string
  storeIds?: string[] | null
  popular?: boolean
  template?: boolean
  keywords?: string[]
}

interface Category {
  id: string
  name: string
  icon: string
  description?: string
  questions: Question[]
}

export const SuggestedQuestions: React.FC<SuggestedQuestionsProps> = ({ onQuestionClick }) => {
  const theme = useTheme()
  const auth = useAuth()
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [showAllPopular, setShowAllPopular] = useState(false)

  // Process questions based on user role scope
  const processedCategories = useMemo(() => {
    const userRoleScope = auth.user?.role?.scope
    const userRestaurants = getUserRestaurants(auth.user)

    return (suggestedQuestionsData.categories as Category[])
      .map(category => ({
        ...category,
        questions: category.questions
          .filter(() => {
            // Filter role-specific questions
            if (category.id === 'restaurant-specific' && userRoleScope !== 'restaurant') {
              // Only show restaurant-specific questions to restaurant scope users
              return false
            }

            return true
          })
          .map(q => {
            // Replace template variables with actual restaurant IDs
            let processedText = q.text

            if (q.template && q.storeIds) {
              if (userRoleScope === 'restaurant' && userRestaurants.length > 0) {
                // Replace {id} with actual restaurant IDs from user's restaurants
                const restaurantIds = userRestaurants.slice(0, q.storeIds.length).map(r => r.id || r.restaurantId)

                q.storeIds.forEach((placeholder, index) => {
                  if (restaurantIds[index]) {
                    processedText = processedText.replace(placeholder, String(restaurantIds[index]))
                  }
                })
              } else {
                // For non-restaurant scope users, show generic template
                // Remove template questions or show them with placeholders
                return null
              }
            }

            return {
              ...q,
              text: processedText
            }
          })
          .filter((q): q is Question => q !== null)
      }))
      .filter(category => category.questions.length > 0)
  }, [auth.user])

  // Get top questions (show popular ones first)
  const topQuestions = useMemo(() => {
    const allQuestions: Question[] = []
    ;(suggestedQuestionsData.categories as Category[]).forEach(category => {
      category.questions
        .filter(q => q.popular)
        .forEach(q => {
          let text = q.text

          // Process templates for restaurant scope users
          if (q.template && auth.user?.role?.scope === 'restaurant') {
            const userRestaurants = getUserRestaurants(auth.user)
            if (userRestaurants.length > 0 && q.storeIds) {
              const restaurantIds = userRestaurants.slice(0, q.storeIds.length).map(r => String(r.id || r.restaurantId))

              q.storeIds.forEach((placeholder, index) => {
                if (restaurantIds[index]) {
                  text = text.replace(placeholder, restaurantIds[index])
                }
              })
            } else {
              return // Skip if no restaurants available
            }
          }

          allQuestions.push({ ...q, text })
        })
    })

    return allQuestions
  }, [auth.user])

  const handleQuestionClick = (question: string) => {
    onQuestionClick(question)
  }

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev)

      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }

      return next
    })
  }

  const displayedPopularQuestions = showAllPopular ? topQuestions : topQuestions.slice(0, 3)
  const hasMorePopular = topQuestions.length > 3

  return (
    <Box sx={{ width: '100%' }}>
      {/* Popular Questions - Compact Horizontal Scroll */}
      {topQuestions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant='caption'
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Popular
            </Typography>
            {hasMorePopular && (
              <Typography
                variant='caption'
                onClick={() => setShowAllPopular(!showAllPopular)}
                sx={{
                  cursor: 'pointer',
                  color: 'primary.main',
                  fontSize: '0.7rem',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {showAllPopular ? 'Show less' : `+${topQuestions.length - 3} more`}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {displayedPopularQuestions.map((question, idx) => (
              <Paper
                key={`top-${idx}`}
                elevation={0}
                onClick={() => handleQuestionClick(question.text)}
                sx={{
                  p: 0.75,
                  px: 1.25,
                  cursor: 'pointer',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                  transition: 'all 0.2s ease-in-out',
                  flex: '0 0 auto',
                  maxWidth: 'calc(50% - 6px)',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.primary',
                    fontSize: '0.75rem',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {question.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Categories - Collapsible */}
      {processedCategories.map(category => {
        const isExpanded = expandedCategories.has(category.id)
        const displayedQuestions = isExpanded ? category.questions : category.questions.slice(0, 2)
        const hasMore = category.questions.length > 2

        return (
          <Box key={category.id} sx={{ mb: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 0.5,
                px: 0.75,
                borderRadius: 0.5,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)'
                }
              }}
              onClick={() => toggleCategory(category.id)}
            >
              <Typography
                variant='caption'
                sx={{
                  fontWeight: 500,
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </Typography>
              <IconButton
                size='small'
                sx={{
                  p: 0.25,
                  color: 'text.secondary',
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
              >
                <Icon icon='tabler:chevron-right' fontSize={14} />
              </IconButton>
            </Box>
            <Collapse in={isExpanded || !hasMore}>
              <Box sx={{ mt: 0.5, pl: 1 }}>
                {displayedQuestions.map((question, idx) => (
                  <Paper
                    key={`${category.id}-${idx}`}
                    elevation={0}
                    onClick={() => handleQuestionClick(question.text)}
                    sx={{
                      p: 0.75,
                      mb: 0.5,
                      cursor: 'pointer',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 0.75,
                      bgcolor: 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.01)',
                        transform: 'translateX(2px)'
                      }
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {question.text}
                    </Typography>
                  </Paper>
                ))}
                {!isExpanded && hasMore && (
                  <Typography
                    variant='caption'
                    onClick={e => {
                      e.stopPropagation()
                      toggleCategory(category.id)
                    }}
                    sx={{
                      display: 'block',
                      cursor: 'pointer',
                      color: 'primary.main',
                      fontSize: '0.7rem',
                      mt: 0.5,
                      pl: 0.75,
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    +{category.questions.length - 2} more
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Box>
        )
      })}
    </Box>
  )
}
